// banking/src/components/dashboard/swap/FiatToCryptoModal.tsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { X } from 'lucide-react';
import { swapService } from '../../../services/swap.service';
import type { RootState } from '../../../store';

interface FiatToCryptoModalProps {
  isOpen: boolean;
  onClose: () => void;
  zenusAccountId?: string;
  zenusBalance: number;
  onSuccess: () => void;
}

interface WalletInfo {
  address: string;
  balance: string;
  currency: string;
  displayName: string;
  network: string;
}

interface FiatWallet {
  id: string;
  name: string;
  balance: number;
  currency: string;
}

const FiatToCryptoModal: React.FC<FiatToCryptoModalProps> = ({
  isOpen,
  onClose,
  zenusAccountId,
  zenusBalance,
  onSuccess
}) => {
  const { t } = useTranslation();
  const { data: { cryptoWallets, zenusAccount } } = useSelector((state: RootState) => state.assets);

  const [selectedFiatWallet, setSelectedFiatWallet] = useState<string>('');
  const [selectedCryptoWallet, setSelectedCryptoWallet] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [fiatWallets, setFiatWallets] = useState<FiatWallet[]>([]);
  const [cryptoWalletsList, setCryptoWalletsList] = useState<WalletInfo[]>([]);

  // Cargar wallets cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadWallets();
    } else {
      // Reset form
      setSelectedFiatWallet('');
      setSelectedCryptoWallet('');
      setAmount('');
    }
  }, [isOpen, zenusAccount, cryptoWallets]);

  const loadWallets = () => {
    // Cargar Fiat Wallets (actualmente solo Zenus USD)
    const fiatList: FiatWallet[] = [];

    if (zenusAccount && zenusAccount.account && zenusAccount.balance !== undefined) {
      fiatList.push({
        id: zenusAccount.account.id || 'zenus-usd',
        name: 'USA Account',
        balance: zenusAccount.balance,
        currency: 'USD'
      });
    }

    setFiatWallets(fiatList);

    // Auto-seleccionar la primera (y única por ahora) wallet fiat
    if (fiatList.length > 0) {
      setSelectedFiatWallet(fiatList[0].id);
    }

    // Cargar Crypto Wallets
    if (cryptoWallets?.data) {
      const cryptoList: WalletInfo[] = [];

      // Bitcoin - Show all wallets, even with 0 balance
      (cryptoWallets.data.bitcoin || []).forEach((wallet: any) => {
        if (wallet.balances?.btc !== undefined) {
          cryptoList.push({
            address: wallet.address,
            balance: wallet.balances.btc,
            currency: 'BTC',
            displayName: 'Bitcoin (BTC)',
            network: 'BITCOIN'
          });
        }
      });

      // Ethereum + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.ethereum || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.eth !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.eth,
              currency: 'ETH',
              displayName: 'Ethereum (ETH)',
              network: 'ETHEREUM'
            });
          }
          if (wallet.balances.usdt_erc20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_erc20,
              currency: 'USDT_ERC20',
              displayName: 'USDT (ERC20)',
              network: 'ETHEREUM'
            });
          }
          if (wallet.balances.usdc_erc20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdc_erc20,
              currency: 'USDC_ERC20',
              displayName: 'USDC (ERC20)',
              network: 'ETHEREUM'
            });
          }
        }
      });

      // BNB + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.bnb || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.bnb !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.bnb,
              currency: 'BNB',
              displayName: 'BNB Chain (BNB)',
              network: 'BNB'
            });
          }
          if (wallet.balances.usdt_bep20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_bep20,
              currency: 'USDT_BEP20',
              displayName: 'USDT (BEP20)',
              network: 'BNB'
            });
          }
        }
      });

      // Tron + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.tron || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.trx !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.trx,
              currency: 'TRX',
              displayName: 'Tron (TRX)',
              network: 'TRON'
            });
          }
          if (wallet.balances.usdt_trc20 !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_trc20,
              currency: 'USDT_TRC20',
              displayName: 'USDT (TRC20)',
              network: 'TRON'
            });
          }
        }
      });

      // Solana + tokens - Show all wallets, even with 0 balance
      (cryptoWallets.data.solana || []).forEach((wallet: any) => {
        if (wallet.balances) {
          if (wallet.balances.sol !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.sol,
              currency: 'SOL',
              displayName: 'Solana (SOL)',
              network: 'SOLANA'
            });
          }
          if (wallet.balances.usdt_spl !== undefined) {
            cryptoList.push({
              address: wallet.address,
              balance: wallet.balances.usdt_spl,
              currency: 'USDT_SPL',
              displayName: 'USDT (Solana)',
              network: 'SOLANA'
            });
          }
        }
      });

      setCryptoWalletsList(cryptoList);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!selectedFiatWallet) {
      toast.error(t('swap.fiatToCrypto.errors.selectFiatWallet') || 'Please select a USD account');
      return;
    }

    if (!selectedCryptoWallet) {
      toast.error(t('swap.fiatToCrypto.errors.selectCryptoWallet') || 'Please select a crypto wallet');
      return;
    }

    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum)) {
      toast.error(t('swap.fiatToCrypto.errors.invalidAmount') || 'Invalid amount');
      return;
    }

    if (amountNum < 100) {
      toast.error(t('swap.fiatToCrypto.errors.minimumAmount') || 'Minimum amount is $100 USD');
      return;
    }

    const selectedFiat = fiatWallets.find(w => w.id === selectedFiatWallet);
    if (!selectedFiat || amountNum > selectedFiat.balance) {
      toast.error(t('swap.fiatToCrypto.errors.insufficientBalance') || 'Insufficient balance');
      return;
    }

    const selectedCrypto = cryptoWalletsList.find(w => `${w.currency}-${w.address}` === selectedCryptoWallet);
    if (!selectedCrypto) {
      toast.error(t('swap.fiatToCrypto.errors.walletNotFound') || 'Wallet not found');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await swapService.createFiatToCrypto({
        fromAccountId: selectedFiatWallet,
        fromAmount: amountNum,
        toWalletAddress: selectedCrypto.address,
        toCurrency: selectedCrypto.currency
      });

      if (response.success) {
        toast.success(t('swap.fiatToCrypto.success') || 'SWAP request created successfully');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating SWAP:', error);
      toast.error(error.message || t('swap.fiatToCrypto.errors.generic') || 'Error creating SWAP request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMaxAmount = () => {
    const selectedFiat = fiatWallets.find(w => w.id === selectedFiatWallet);
    if (selectedFiat) {
      setAmount(selectedFiat.balance.toString());
    }
  };

  if (!isOpen) return null;

  const selectedFiat = fiatWallets.find(w => w.id === selectedFiatWallet);
  const selectedCrypto = cryptoWalletsList.find(w => `${w.currency}-${w.address}` === selectedCryptoWallet);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">
            {t('swap.fiatToCrypto.title') || 'SWAP: FIAT → CRYPTO'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* From: USD Account */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('swap.fiatToCrypto.fromAccount') || 'From (USD Account)'} <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedFiatWallet}
              onChange={(e) => setSelectedFiatWallet(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('swap.fiatToCrypto.selectAccount') || 'Select account'}</option>
              {fiatWallets.map((wallet) => (
                <option
                  key={wallet.id}
                  value={wallet.id}
                  disabled={wallet.balance <= 0}
                  style={{ color: wallet.balance <= 0 ? '#999' : 'inherit' }}
                >
                  {wallet.name} - ${wallet.balance.toFixed(2)} {wallet.currency} {wallet.balance <= 0 ? '(Insufficient balance)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('swap.fiatToCrypto.amountLabel') || 'Amount (USD)'} <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="100"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('swap.fiatToCrypto.enterAmount') || 'Enter amount'}
                required
              />
              <button
                type="button"
                onClick={handleMaxAmount}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-600 hover:text-blue-700 font-semibold text-sm"
              >
                MAX
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('swap.fiatToCrypto.info.minimum') || 'Minimum: $100 USD'}
            </p>
          </div>

          {/* To: Crypto Wallet */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('swap.fiatToCrypto.toWallet') || 'To (Crypto Wallet)'} <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedCryptoWallet}
              onChange={(e) => setSelectedCryptoWallet(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">{t('swap.fiatToCrypto.selectWallet') || 'Select crypto wallet'}</option>
              {cryptoWalletsList.map((wallet) => (
                <option key={`${wallet.currency}-${wallet.address}`} value={`${wallet.currency}-${wallet.address}`}>
                  {wallet.displayName} - {wallet.address.slice(0, 6)}...{wallet.address.slice(-4)} - {parseFloat(wallet.balance).toFixed(8)}
                </option>
              ))}
            </select>
            {cryptoWalletsList.length === 0 && (
              <p className="text-xs text-red-500 mt-1">
                {t('swap.fiatToCrypto.errors.noCryptoWallets') || 'You don\'t have any crypto wallets. Please create one first.'}
              </p>
            )}
          </div>

          {/* Summary */}
          {selectedFiat && selectedCrypto && amount && parseFloat(amount) >= 100 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">
                {t('swap.summary.title') || 'Summary'}
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.from') || 'From'}:</span>
                  <span className="font-medium">{selectedFiat.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.amount') || 'Amount'}:</span>
                  <span className="font-medium">${parseFloat(amount).toFixed(2)} USD</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.to') || 'To'}:</span>
                  <span className="font-medium">{selectedCrypto.displayName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">{t('swap.summary.address') || 'Address'}:</span>
                  <span className="font-mono text-xs">{selectedCrypto.address.slice(0, 10)}...{selectedCrypto.address.slice(-8)}</span>
                </div>
                <div className="border-t border-blue-300 pt-2 mt-2">
                  <p className="text-xs text-blue-700">
                    {t('swap.fiatToCrypto.info.commission') || 'Commission: ~2% (adjustable by staff)'}
                  </p>
                  <p className="text-xs text-blue-700">
                    {t('swap.fiatToCrypto.info.processing') || 'Processing time: 1-24 hours'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              {t('swap.fiatToCrypto.infoMessage') ||
                'A support ticket will be created automatically. Our team will contact you with the final details of the operation.'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || cryptoWalletsList.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting
                ? (t('common.loading') || 'Loading...')
                : (t('swap.fiatToCrypto.submit') || 'Create SWAP Request')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FiatToCryptoModal;
